import { getEstimatedDeliveryDaysMin } from '~/lib/quotes';
import { BasketQuote } from '~/lib/types/basket-types';
import { onlyUniqueNonBlank } from '~/lib/utils/array';

export enum EstimatedDeliveryGroupTranslationKey {
  DomesticNextDay = 'FILTER_ESTIMATED_DELIVERY_OPTION_DOMESTIC_NEXT_DAY',
  Domestic2Days = 'FILTER_ESTIMATED_DELIVERY_OPTION_DOMESTIC_2_DAYS',
  Domestic3PlusDays = 'FILTER_ESTIMATED_DELIVERY_OPTION_DOMESTIC_3_PLUS_DAYS',
  International1_2Days = 'FILTER_ESTIMATED_DELIVERY_OPTION_INTERNATIONAL_1_2_DAYS',
  International3PlusDays = 'FILTER_ESTIMATED_DELIVERY_OPTION_INTERNATIONAL_3_PLUS_DAYS',
  International5PlusDays = 'FILTER_ESTIMATED_DELIVERY_OPTION_INTERNATIONAL_5_PLUS_DAYS'
}

/*
  Generate a fallback label for the estimated delivery group based on the translation key.
*/
export const generateEstimatedDeliveryGroupLabel = (translationKey: EstimatedDeliveryGroupTranslationKey | string) => {
  switch (translationKey) {
    case EstimatedDeliveryGroupTranslationKey.DomesticNextDay:
      return 'Next Day+';

    case EstimatedDeliveryGroupTranslationKey.Domestic2Days:
      return '2 Day+';

    case EstimatedDeliveryGroupTranslationKey.Domestic3PlusDays:
      return '3 Day+';

    case EstimatedDeliveryGroupTranslationKey.International1_2Days:
      return 'Express 1-2 Day+';

    case EstimatedDeliveryGroupTranslationKey.International3PlusDays:
      return 'Economy 3 Day+';

    case EstimatedDeliveryGroupTranslationKey.International5PlusDays:
      return 'Super Economy 5 Day+';

    default:
      return '';
  }
};

/*
  Based on the translation key for the filters options, generate the title and title excluding VAT translation keys.
*/
export const generateEstimatedDeliveryGroupTitleLabel = (
  categoryTranslationKey: EstimatedDeliveryGroupTranslationKey | string
): { titleTranslationKey: string; titleExcVatTranslationKey: string } => {
  switch (categoryTranslationKey) {
    case EstimatedDeliveryGroupTranslationKey.DomesticNextDay:
      return {
        titleTranslationKey: 'RESULT_SPEED_DOMESTIC_NEXT_DAY_TITLE',
        titleExcVatTranslationKey: 'RESULT_SPEED_DOMESTIC_NEXT_DAY_EXC_VAT_TITLE'
      };

    case EstimatedDeliveryGroupTranslationKey.Domestic2Days:
      return {
        titleTranslationKey: 'RESULT_SPEED_DOMESTIC_2_DAYS_TITLE',
        titleExcVatTranslationKey: 'RESULT_SPEED_DOMESTIC_2_DAYS_EXC_VAT_TITLE'
      };

    case EstimatedDeliveryGroupTranslationKey.Domestic3PlusDays:
      return {
        titleTranslationKey: 'RESULT_SPEED_DOMESTIC_3_PLUS_DAYS_TITLE',
        titleExcVatTranslationKey: 'RESULT_SPEED_DOMESTIC_3_PLUS_DAYS_EXC_VAT_TITLE'
      };

    case EstimatedDeliveryGroupTranslationKey.International1_2Days:
      return {
        titleTranslationKey: 'RESULT_SPEED_INTERNATIONAL_1_2_DAYS_TITLE',
        titleExcVatTranslationKey: 'RESULT_SPEED_INTERNATIONAL_1_2_DAYS_EXC_VAT_TITLE'
      };

    case EstimatedDeliveryGroupTranslationKey.International3PlusDays:
      return {
        titleTranslationKey: 'RESULT_SPEED_INTERNATIONAL_3_PLUS_DAYS_TITLE',
        titleExcVatTranslationKey: 'RESULT_SPEED_INTERNATIONAL_3_PLUS_DAYS_EXC_VAT_TITLE'
      };

    case EstimatedDeliveryGroupTranslationKey.International5PlusDays:
      return {
        titleTranslationKey: 'RESULT_SPEED_INTERNATIONAL_5_PLUS_DAYS_TITLE',
        titleExcVatTranslationKey: 'RESULT_SPEED_INTERNATIONAL_5_PLUS_DAYS_EXC_VAT_TITLE'
      };

    default:
      return {
        titleTranslationKey: '',
        titleExcVatTranslationKey: ''
      };
  }
};

/*
  Generates groups of estimated delivery days based on logical groups eg: NextDay, 2Day, 3PlusDays, Economy etc
  This includes translation keys for filter tags and section headers.
*/
export const generateEstimatedDeliveryGroups = (
  quotes: BasketQuote[],
  isDomestic: boolean
): {
  titleTranslationKey: string;
  titleExcVatTranslationKey: string;
  translationKey: EstimatedDeliveryGroupTranslationKey;
  days: number[];
  type: string;
}[] => {
  const allDays = quotes.map(getEstimatedDeliveryDaysMin).filter(onlyUniqueNonBlank);
  const output: {
    translationKey: EstimatedDeliveryGroupTranslationKey;
    days: number[];
    type?: string;
  }[] = [];

  if (isDomestic) {
    // Next Day+ services
    // 2 Day+ services
    // 3 Day+ services
    if (allDays.includes(1)) {
      output.push({
        translationKey: EstimatedDeliveryGroupTranslationKey.DomesticNextDay,
        days: [1]
      });
    }

    if (allDays.includes(2)) {
      output.push({
        translationKey: EstimatedDeliveryGroupTranslationKey.Domestic2Days,
        days: [2]
      });
    }

    if (allDays.some((d) => d > 2)) {
      const days = allDays.filter((d) => d > 2).sort();
      output.push({
        translationKey: EstimatedDeliveryGroupTranslationKey.Domestic3PlusDays,
        days: days
      });
    }
  } else {
    // Express 1-2 Day Services
    // Economy 3+ Days
    // Super Economy 5+ Days
    if (allDays.some((d) => d <= 2)) {
      output.push({
        translationKey: EstimatedDeliveryGroupTranslationKey.International1_2Days,
        days: [1, 2]
      });
    }

    if (allDays.some((d) => d >= 3 && d <= 4)) {
      output.push({
        translationKey: EstimatedDeliveryGroupTranslationKey.International3PlusDays,
        days: [3, 4]
      });
    }

    if (allDays.some((d) => d > 4)) {
      const days = allDays.filter((d) => d > 4).sort();
      output.push({
        translationKey: EstimatedDeliveryGroupTranslationKey.International5PlusDays,
        days: days
      });
    }
  }

  return output.map((x) => ({
    ...x,
    ...generateEstimatedDeliveryGroupTitleLabel(x.translationKey),
    type: x.days.sort().join('_')
  }));
};
